/**
 * View-count display helper.
 *
 * Public-facing view counts are shown with a per-article random baseline
 * (between 50,000 and 1,000,000) added on top of the real (backend)
 * view count, formatted compactly (e.g. "312K", "51.2K", "1M").
 * The baseline is display-only — the backend / admin analytics never
 * see it, so editorial performance numbers stay truthful.
 *
 * The baseline is derived deterministically from the article ID so every
 * render of the same article shows the same number.
 *
 * Usage:
 *   import { formatDisplayViews, getDisplayViews } from '@/lib/view-count';
 *   formatDisplayViews(article.views, article.id)   // => "312K"
 *   getDisplayViews(article.views, article.id)       // => 312127  (raw number)
 *
 * DO NOT use this helper in the admin dashboard / analytics pages —
 * those show raw `.toLocaleString()` backend numbers so editors can
 * see real performance.
 */

export const VIEW_BASELINE_MIN = 50000;
export const VIEW_BASELINE_MAX = 1000000;

/**
 * Simple deterministic hash of a string (djb2 algorithm).
 * Returns a non-negative 31-bit integer.
 */
function hashString(s) {
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash + s.charCodeAt(i)) & 0x7FFFFFFF;
  }
  return hash;
}

/**
 * Return a per-article baseline between VIEW_BASELINE_MIN and
 * VIEW_BASELINE_MAX, deterministic for the given articleId.
 */
export function getArticleBaseline(articleId) {
  if (!articleId) return VIEW_BASELINE_MIN;
  const hash = hashString(String(articleId));
  return VIEW_BASELINE_MIN + (hash % (VIEW_BASELINE_MAX - VIEW_BASELINE_MIN + 1));
}

/**
 * Return the number we want to show the public:
 * per-article baseline + real views.
 * Always returns a finite non-negative integer.
 */
export function getDisplayViews(realViews, articleId) {
  const n = Number(realViews);
  const safe = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  return getArticleBaseline(articleId) + safe;
}

/**
 * Compact number formatter: 50000 → "50K", 51234 → "51.2K",
 * 1_234_567 → "1.2M", 12_345_678 → "12.3M". Falls back to a
 * plain toLocaleString on very old browsers that don't support the
 * `compact` notation.
 */
export function formatCompact(n) {
  const num = Number(n) || 0;
  try {
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(num);
  } catch {
    return num.toLocaleString();
  }
}

/**
 * One-shot: take the raw backend `views` and an `articleId`, and return
 * the public display string with per-article baseline.
 */
export function formatDisplayViews(realViews, articleId) {
  return formatCompact(getDisplayViews(realViews, articleId));
}
